import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, WebhookEvent, webhookEventList, withStyles } from 'vopay';
import { Button, Checkbox, Table } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useToast } from '#shared/providers/Toast';
import useFetchWebhookInfo from '#shared/hooks/useApi/useFetchWebhookInfo';
import useUpdateWebhook from '#shared/hooks/useApi/useUpdateWebhook';
import useTestWebhook from '#shared/hooks/useApi/useTestWebhook';
import WebhooksTableSkeleton from '../WebhooksTableSkeleton';
import useBuildUpdateWebhookRows from './buildUpdateWebhooksRows.helper';
import useUpdateWebhooksFormConfig, { WebhookFormValues } from './useUpdateWebhooksFormConfig';
import styles from './UpdateWebhooksPage.module.scss';

const cn = withStyles(styles);

// Seeds every event's url/Enabled field so the form builder has a complete shape before real data loads.
function buildEmptyValues(): WebhookFormValues {
  const values = {} as WebhookFormValues;

  webhookEventList.forEach((event) => {
    values[event] = '';
    values[`${event}Enabled`] = false;
  });

  return values;
}

const EMPTY_VALUES = buildEmptyValues();

type UpdateWebhooksPageProps = {
  username: string;
  events: readonly WebhookEvent[];
  readOnly?: boolean;
  onShowHistory: () => void;
};

export default function UpdateWebhooksPage({
  username,
  events,
  readOnly = false,
  onShowHistory,
}: UpdateWebhooksPageProps) {
  const { fetchingWebhooks, webhooks, fetchWebhookInfo } = useFetchWebhookInfo();
  const { updateWebhook } = useUpdateWebhook();
  const { testWebhook } = useTestWebhook();
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const webhookRows = useBuildUpdateWebhookRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks.Update' });

  const [selectedEvents, setSelectedEvents] = useState<Set<WebhookEvent>>(new Set());
  const [fetchFailed, setFetchFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTestingEvents, setIsTestingEvents] = useState(false);

  // Maps event id -> label so failure messages can show a readable event name.
  const labelByEvent = useMemo(
    () => Object.fromEntries(webhookRows.map((row) => [String(row.id), row.label])),
    [webhookRows],
  );

  useEffect(() => {
    fetchWebhookInfo({ PortalUser: username })
      .then(() => setFetchFailed(false))
      .catch((error) => {
        setFetchFailed(true);

        toast.neutral(
          t('fetchError', {
            defaultValue: 'Could not load webhooks: {{errorMessage}}',
            errorMessage: error instanceof Error ? error.message : t('unknownError', { defaultValue: 'Unknown error' }),
          }),
        );
      });
  }, []);

  const initialValues = useMemo<WebhookFormValues>(() => {
    const values = { ...EMPTY_VALUES };

    (webhooks || []).forEach((webhook) => {
      values[webhook.Event] = webhook.Url;
      values[`${webhook.Event}Enabled`] = !!webhook.Url;
    });

    return values;
  }, [webhooks]);

  async function handleUpdate(values: WebhookFormValues) {
    setIsSaving(true);

    try {
      const settled = await Promise.allSettled(
        events.map((event) =>
          updateWebhook({
            PortalUser: username,
            Type: event,
            WebHookUrl: values[`${event}Enabled`] ? values[event] : '',
            Disabled: !values[`${event}Enabled`],
          }),
        ),
      );

      const results = settled.map((result, index) => {
        const event = events[index];

        if (result.status === 'fulfilled') {
          return { event, success: result.value.Success, message: result.value.ErrorMessage };
        }

        return { event, success: false, message: result.reason instanceof Error ? result.reason.message : '' };
      });

      const failures = results.filter((result) => !result.success);

      if (failures.length === 0) {
        const changedEvents = events.filter(
          (event) => !!initialValues[`${event}Enabled`] !== !!values[`${event}Enabled`],
        );

        const activatedEvents = changedEvents.filter((event) => values[`${event}Enabled`]);
        const deactivatedEvents = changedEvents.filter((event) => !values[`${event}Enabled`]);

        if (activatedEvents.length > 0 && deactivatedEvents.length === 0) {
          toast.success(t('updateSuccessToast', { defaultValue: 'Your webhooks have been activated' }));
        } else if (deactivatedEvents.length > 0 && activatedEvents.length === 0) {
          toast.success(t('updateDeactivatedToast', { defaultValue: 'Your webhooks have been deactivated' }));
        } else {
          toast.success(t('updateUpdatedToast', { defaultValue: 'Your webhooks have been updated' }));
        }

        try {
          await fetchWebhookInfo({ PortalUser: username });
        } catch (error) {
          toast.neutral(
            t('fetchError', {
              defaultValue: 'Could not load webhooks: {{errorMessage}}',
              errorMessage:
                error instanceof Error ? error.message : t('unknownError', { defaultValue: 'Unknown error' }),
            }),
          );
        }

        return;
      }

      await alertDialog.error(
        t('Dialogs.UpdateError.title', { defaultValue: 'Error! The webhook was not updated.' }),
        <ul style={{ textAlign: 'left', margin: 0 }}>
          {failures.map((failure) => (
            <li key={failure.event}>{`${labelByEvent[failure.event]}: ${failure.message}`}</li>
          ))}
        </ul>,
      );
    } finally {
      setIsSaving(false);
    }
  }

  const { ctx, rows } = useUpdateWebhooksFormConfig({
    events,
    initialValues,
    onSubmit: handleUpdate,
    readOnly,
  });

  // Testable = saved as active with a URL, and not edited since. The test endpoint only takes the event type and
  // sends to the stored webhook, so an unsaved row would test the old URL or fail with "Webhook URL is not set up".
  const testableEvents = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            initialValues[`${row.event}Enabled`] &&
            initialValues[row.event] &&
            ctx.values[`${row.event}Enabled`] === initialValues[`${row.event}Enabled`] &&
            ctx.values[row.event] === initialValues[row.event],
        )
        .map((row) => row.event),
    [rows, ctx.values, initialValues],
  );

  // A row can be selected and then edited, so only send events that are still testable.
  const selectedTestableEvents = testableEvents.filter((event) => selectedEvents.has(event));

  const hasTestableRows = !readOnly && testableEvents.length > 0;

  const allTestableSelected = hasTestableRows && selectedTestableEvents.length === testableEvents.length;

  function toggleSelectAllTestable() {
    setSelectedEvents(allTestableSelected ? new Set() : new Set(testableEvents));
  }

  function toggleSelectEvent(event: WebhookEvent) {
    setSelectedEvents((prev) => {
      const next = new Set(prev);

      if (next.has(event)) {
        next.delete(event);
      } else {
        next.add(event);
      }

      return next;
    });
  }

  async function handleSendTestEvents() {
    const events = selectedTestableEvents;

    setIsTestingEvents(true);

    try {
      const settled = await Promise.allSettled(
        events.map((event) => testWebhook({ PortalUser: username, Type: event })),
      );

      const results = settled.map((result, index) => {
        const event = events[index];

        if (result.status === 'fulfilled') {
          return { event, success: result.value.Success, message: result.value.ErrorMessage };
        }

        return { event, success: false, message: result.reason instanceof Error ? result.reason.message : '' };
      });

      const failures = results.filter((result) => !result.success);

      if (failures.length === 0) {
        toast.success(
          <Trans
            t={t}
            i18nKey="testSuccessToast"
            defaults="Test event sent to your endpoints. See <0>Webhooks History</0> for results."
            components={[
              <button type="button" key="webhooks-history-link" className={cn('toastLink')} onClick={onShowHistory} />,
            ]}
          />,
        );

        setSelectedEvents(new Set());

        return;
      }

      await alertDialog.error(
        t('Dialogs.TestError.title', { defaultValue: 'Error! Some test events were not sent.' }),
        <ul style={{ textAlign: 'left', margin: 0 }}>
          {failures.map((failure) => (
            <li key={failure.event}>{`${labelByEvent[failure.event]}: ${failure.message}`}</li>
          ))}
        </ul>,
      );
    } finally {
      setIsTestingEvents(false);
    }
  }

  if (fetchingWebhooks && !webhooks) return <WebhooksTableSkeleton />;

  if (fetchFailed) {
    return (
      <Box flx="col" ai="center" jc="center" gapSize="16px" pt="lg" pb="lg">
        <span>{t('loadError', { defaultValue: 'Could not load your webhooks.' })}</span>
      </Box>
    );
  }

  return (
    <form onSubmit={ctx.handleSubmit}>
      <Table
        id="update-webhook-fields"
        zebra
        columns={[
          {
            id: 'select',
            visible: hasTestableRows,
            header: (
              <Checkbox
                name="select-all-testable"
                checked={allTestableSelected}
                label=""
                className={cn('headerCheckbox')}
                onChange={toggleSelectAllTestable}
              />
            ),
            template: ({ data }) => (
              <Checkbox
                name={`select-${data.event}`}
                checked={selectedTestableEvents.includes(data.event)}
                disabled={!testableEvents.includes(data.event)}
                label=""
                onChange={() => toggleSelectEvent(data.event)}
              />
            ),
          },
          {
            id: 'event',
            header: t('Form.event', { defaultValue: 'Event' }),
            align: 'left',
            template: ({ data }) => <label htmlFor={`checkbox-${data.event}Enabled`}>{data.label}</label>,
          },
          {
            id: 'enable',
            header: t('Form.enable', { defaultValue: 'Enable' }),
            align: 'left',
            template: ({ data }) => data.toggleElement,
          },
          {
            id: 'url',
            header: t('Form.url', { defaultValue: 'URL' }),
            align: 'left',
            template: ({ data }) => data.urlElement,
          },
        ]}
        rows={rows.map((row) => ({ id: row.event, data: row }))}
      />

      <Box mt="md" jc="fe" gapSize="8px">
        {hasTestableRows && (
          <Box mb="md" jc="fe">
            <Button
              id="Webhook-send-test-events"
              type="button"
              colorScheme="secondary"
              disabled={selectedTestableEvents.length === 0 || isTestingEvents || isSaving}
              onClick={handleSendTestEvents}
            >
              {t('sendTestEventsButton', { defaultValue: 'Test Events' })}
            </Button>
          </Box>
        )}

        {!readOnly && (
          <Button
            id="Webhook-submit"
            type="submit"
            colorScheme="primary"
            disabled={!ctx.hasPendingData || !ctx.isValid || isSaving || isTestingEvents}
          >
            {t('saveWebhooksButton', { defaultValue: 'Save Webhooks' })}
          </Button>
        )}
      </Box>
    </form>
  );
}
